import { useEffect, useState } from 'react';
import {
  FunnelIcon,
  MagnifyingGlassIcon,
  UserIcon,
  BuildingOfficeIcon,
  CircleStackIcon,
  ShieldCheckIcon,
  Cog6ToothIcon,
} from '@heroicons/react/24/outline';
import { Header } from '@/components/Layout';
import { PageLoader, EmptyState } from '@/components/common';
import { auditApi } from '@/services/api';
import type { AuditLog } from '@/types';

const actionIcons: Record<string, React.ComponentType<React.SVGProps<SVGSVGElement>>> = {
  'user.': UserIcon,
  'org.': BuildingOfficeIcon,
  'db.': CircleStackIcon,
  'role.': ShieldCheckIcon,
  'settings.': Cog6ToothIcon,
};

const actionLabels: Record<string, string> = {
  'user.login': 'Inicio de sesión',
  'user.logout': 'Cierre de sesión',
  'user.created': 'Usuario creado',
  'user.updated': 'Usuario actualizado',
  'user.deleted': 'Usuario eliminado',
  'user.password_changed': 'Contraseña cambiada',
  'org.created': 'Organización creada',
  'org.updated': 'Organización actualizada',
  'org.deleted': 'Organización eliminada',
  'org.suspended': 'Organización suspendida',
  'role.assigned': 'Rol asignado',
  'role.revoked': 'Rol revocado',
  'role.created': 'Rol creado',
  'role.updated': 'Rol actualizado',
  'db.created': 'Base de datos creada',
  'db.backup': 'Backup creado',
  'db.restore': 'Base de datos restaurada',
  'db.deleted': 'Base de datos eliminada',
  'content.created': 'Contenido creado',
  'content.updated': 'Contenido actualizado',
  'content.deleted': 'Contenido eliminado',
  'settings.updated': 'Configuración actualizada',
};

function getActionIcon(action: string) {
  for (const [prefix, Icon] of Object.entries(actionIcons)) {
    if (action.startsWith(prefix)) {
      return Icon;
    }
  }
  return Cog6ToothIcon;
}

function getActionLabel(action: string): string {
  return actionLabels[action] || action;
}

function formatDate(date: string): string {
  const d = new Date(date);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Ahora mismo';
  if (diffMins < 60) return `Hace ${diffMins} min`;
  if (diffHours < 24) return `Hace ${diffHours}h`;
  if (diffDays < 7) return `Hace ${diffDays}d`;

  return d.toLocaleDateString('es-ES', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export default function AuditPage() {
  const [loading, setLoading] = useState(true);
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [filterAction, setFilterAction] = useState('');

  const limit = 50;

  useEffect(() => {
    loadLogs();
  }, [page, filterAction]);

  const loadLogs = async () => {
    try {
      const response = await auditApi.list(page, limit, {
        action: filterAction || undefined,
      });
      setLogs(response.data.logs || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      console.error('Error loading audit logs:', error);
    } finally {
      setLoading(false);
    }
  };

  // Mock data for demo
  const mockLogs: AuditLog[] = logs.length > 0 ? logs : [
    {
      _id: '1',
      action: 'user.login',
      actor_id: 'user1',
      resource_type: 'user',
      resource_id: 'user1',
      details: { email: 'admin@example.com' },
      ip_address: '192.168.1.100',
      timestamp: new Date(Date.now() - 300000).toISOString(),
    },
    {
      _id: '2',
      action: 'org.created',
      actor_id: 'user1',
      organization_id: 'org1',
      resource_type: 'organization',
      resource_id: 'org1',
      details: { name: 'Nueva Web', slug: 'nueva-web' },
      ip_address: '192.168.1.100',
      timestamp: new Date(Date.now() - 3600000).toISOString(),
    },
    {
      _id: '3',
      action: 'db.backup',
      actor_id: 'user1',
      organization_id: 'org1',
      resource_type: 'database',
      resource_id: 'vida_nueva_web',
      details: { backup_name: 'backup_20250113.tar.gz' },
      ip_address: '192.168.1.100',
      timestamp: new Date(Date.now() - 7200000).toISOString(),
    },
    {
      _id: '4',
      action: 'user.created',
      actor_id: 'user1',
      resource_type: 'user',
      resource_id: 'user2',
      details: { email: 'nuevo@example.com' },
      ip_address: '192.168.1.100',
      timestamp: new Date(Date.now() - 86400000).toISOString(),
    },
    {
      _id: '5',
      action: 'role.assigned',
      actor_id: 'user1',
      organization_id: 'org1',
      resource_type: 'user_organization_role',
      resource_id: 'user2',
      details: { roleId: 'editor' },
      ip_address: '192.168.1.100',
      timestamp: new Date(Date.now() - 172800000).toISOString(),
    },
  ];

  const actionTypes = [
    { value: '', label: 'Todas las acciones' },
    { value: 'user.login', label: 'Inicios de sesión' },
    { value: 'user.created', label: 'Usuarios creados' },
    { value: 'org.created', label: 'Organizaciones creadas' },
    { value: 'db.backup', label: 'Backups' },
    { value: 'db.restore', label: 'Restauraciones' },
  ];

  if (loading) {
    return <PageLoader />;
  }

  return (
    <div>
      <Header
        title="Auditoría"
        subtitle="Registro de actividad del sistema"
      />

      <div className="p-6 space-y-6">
        {/* Filters */}
        <div className="card p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                className="input pl-10"
                placeholder="Buscar en logs..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <div className="flex items-center gap-2">
              <FunnelIcon className="w-5 h-5 text-gray-400" />
              <select
                className="input"
                value={filterAction}
                onChange={(e) => {
                  setFilterAction(e.target.value);
                  setPage(1);
                }}
              >
                {actionTypes.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Logs list */}
        {mockLogs.length === 0 ? (
          <EmptyState
            title="No hay registros"
            description="No se encontraron registros de auditoría"
          />
        ) : (
          <div className="card">
            <div className="divide-y divide-gray-200">
              {mockLogs.map((log) => {
                const Icon = getActionIcon(log.action);
                return (
                  <div
                    key={log._id}
                    className="p-4 hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-start gap-4">
                      <div className="p-2 bg-gray-100 rounded-lg">
                        <Icon className="w-5 h-5 text-gray-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between">
                          <div>
                            <p className="font-medium text-gray-900">
                              {getActionLabel(log.action)}
                            </p>
                            <p className="text-sm text-gray-500 mt-1">
                              {log.details && Object.keys(log.details).length > 0 && (
                                <span className="font-mono text-xs bg-gray-100 px-2 py-0.5 rounded mr-2">
                                  {JSON.stringify(log.details).slice(0, 50)}
                                  {JSON.stringify(log.details).length > 50 && '...'}
                                </span>
                              )}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="text-sm text-gray-500">
                              {formatDate(log.timestamp)}
                            </p>
                            {log.ip_address && (
                              <p className="text-xs text-gray-400 mt-1 font-mono">
                                {log.ip_address}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="mt-2 flex items-center gap-4 text-xs text-gray-500">
                          <span>
                            Recurso: <code className="bg-gray-100 px-1 rounded">{log.resource_type}</code>
                          </span>
                          <span>
                            ID: <code className="bg-gray-100 px-1 rounded">{log.resource_id}</code>
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Pagination */}
            {total > limit && (
              <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between">
                <p className="text-sm text-gray-500">
                  Mostrando {(page - 1) * limit + 1} - {Math.min(page * limit, total)} de {total}
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="btn-secondary text-sm"
                  >
                    Anterior
                  </button>
                  <button
                    onClick={() => setPage((p) => p + 1)}
                    disabled={page * limit >= total}
                    className="btn-secondary text-sm"
                  >
                    Siguiente
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
